// Cache local (IndexedDB) pour le mode hors-ligne : conserve les dernières
// réponses de lecture (consultation : stock, clients, historique) et une
// file d'attente des écritures faites sans réseau (réceptions, remises,
// pertes, lots de lavage), rejouées au retour de la connexion par
// lib/sync.ts.
//
// Volontairement sans bibliothèque (idb, dexie...) : l'API IndexedDB brute
// suffit pour deux object stores simples, et ça n'ajoute rien aux
// dépendances npm (même choix que registerSW.ts pour le service worker).

const DB_NAME = 'caissepilot-offline';
const DB_VERSION = 1;
const CACHE_STORE = 'cache';
const OUTBOX_STORE = 'outbox';

export interface QueueItem {
  id: string;
  path: string;
  method: string;
  body: Record<string, unknown>;
  ref: string;
  kind: string;       // 'reception' | 'issue' | 'loss' | 'wash_batch' | 'autre'
  label: string;       // résumé lisible, affiché dans la page Synchronisation
  createdAt: string;
  status: 'pending' | 'error';
  attempts: number;
  lastError?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) { reject(new Error('IndexedDB indisponible')); return; }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(CACHE_STORE)) db.createObjectStore(CACHE_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        const s = db.createObjectStore(OUTBOX_STORE, { keyPath: 'id' });
        s.createIndex('status', 'status');
        s.createIndex('createdAt', 'createdAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function runTx<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        req.onsuccess = () => resolve(req.result as T);
        req.onerror = () => reject(req.error);
      }),
  );
}

// ---- cache de lecture (consultation hors-ligne) ----------------------------
export async function cacheGet(key: string): Promise<{ data: unknown; savedAt: string } | null> {
  try {
    return (await runTx<any>(CACHE_STORE, 'readonly', (s) => s.get(key))) || null;
  } catch {
    return null;
  }
}

export async function cacheSet(key: string, data: unknown): Promise<void> {
  try {
    await runTx(CACHE_STORE, 'readwrite', (s) => s.put({ key, data, savedAt: new Date().toISOString() }));
  } catch {
    /* stockage indisponible (navigation privée, quota...) : on continue sans cache */
  }
}

// ---- file d'attente des écritures hors-ligne --------------------------------
export async function queueWrite(
  path: string,
  body: Record<string, unknown>,
  ref: string,
  kind: string,
  label: string,
): Promise<QueueItem> {
  const item: QueueItem = {
    id: ref, path, method: 'POST', body, ref, kind, label,
    createdAt: new Date().toISOString(), status: 'pending', attempts: 0,
  };
  await runTx(OUTBOX_STORE, 'readwrite', (s) => s.put(item));
  return item;
}

export async function listQueue(): Promise<QueueItem[]> {
  try {
    const items = await runTx<QueueItem[]>(OUTBOX_STORE, 'readonly', (s) => s.getAll());
    return (items || []).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  } catch {
    return [];
  }
}

export async function removeQueueItem(id: string): Promise<void> {
  try { await runTx(OUTBOX_STORE, 'readwrite', (s) => s.delete(id)); } catch { /* ignore */ }
}

export async function markQueueItem(id: string, patch: Partial<QueueItem>): Promise<void> {
  try {
    const item = await runTx<QueueItem | undefined>(OUTBOX_STORE, 'readonly', (s) => s.get(id));
    if (!item) return;
    await runTx(OUTBOX_STORE, 'readwrite', (s) => s.put({ ...item, ...patch }));
  } catch {
    /* ignore */
  }
}
